import React, { useEffect, useMemo, useState } from "react";
import empresasService from "../../../../services/empresasService";
import cursosService from "../../../../services/cursosService";
import { listarMetodos } from "../../../../services/pagoMetodosService";
import { buscarMatriculas } from "../../../../services/matriculasService";
import cuentasPorCobrarService from "../../../../services/cuentasPorCobrarService";
import { ModalWrapper, SearchableSelect, MatriculasPicker } from "./PagoEmpresaModal";
import { showToast } from "../../../../utils/showToast";

const currency = (v) => `$${Number(v || 0).toFixed(2)}`;
let _lineaSeq = 0;
const nuevaLinea = () => ({ _id: `linea-${++_lineaSeq}`, cursoId: null, cursoNombre: "", cursoSearch: "", cantidad: "1", precioUnitario: "" });

// ══════════════════════════════════════════════════════════════
// MODO: CREAR — facturar a crédito (curso/nivel + cupos, sin matrícula aún)
// ══════════════════════════════════════════════════════════════
function CrearCuentaPorCobrar({ onClose, onSuccess }) {
  const [empresas, setEmpresas] = useState([]);
  const [cursos, setCursos] = useState([]);
  const [empresaSearch, setEmpresaSearch] = useState("");
  const [empresaId, setEmpresaId] = useState(null);
  const [empresaNombre, setEmpresaNombre] = useState("");
  const [observaciones, setObservaciones] = useState("");
  const [lineas, setLineas] = useState([nuevaLinea()]);
  const [submitting, setSubmitting] = useState(false);
  const [resultado, setResultado] = useState(null);

  useEffect(() => {
    empresasService.listar().then((data) => setEmpresas(Array.isArray(data) ? data : [])).catch(() => {});
    cursosService.listar().then((data) => setCursos(Array.isArray(data) ? data : (data?.items || []))).catch(() => {});
  }, []);

  const empresasSugeridas = useMemo(() => {
    const t = empresaSearch.trim().toLowerCase();
    return (t ? empresas.filter((e) => e.nombre.toLowerCase().includes(t)) : empresas)
      .map((e) => ({ id: e.id, label: `${e.nombre}${e.ruc ? ` — ${e.ruc}` : ""}` }));
  }, [empresas, empresaSearch]);

  const cursosSugeridosPorLinea = (search) => {
    const t = search.trim().toLowerCase();
    return (t ? cursos.filter((c) => c.nombre.toLowerCase().includes(t)) : cursos)
      .slice(0, 30)
      .map((c) => ({ id: c.id, label: `${c.nombre}${c.codigo_curso ? ` (${c.codigo_curso})` : ""}` }));
  };

  const actualizarLinea = (id, cambios) => {
    setLineas((prev) => prev.map((l) => (l._id === id ? { ...l, ...cambios } : l)));
  };

  const agregarLinea = () => setLineas((prev) => [...prev, nuevaLinea()]);
  const quitarLinea = (id) => setLineas((prev) => (prev.length > 1 ? prev.filter((l) => l._id !== id) : prev));

  const montoEstimado = lineas.reduce((acc, l) => {
    const cantidad = Number(l.cantidad) || 0;
    const precio = Number(l.precioUnitario) || 0;
    return acc + cantidad * precio;
  }, 0);

  const lineasValidas = lineas.every((l) => l.cursoId && Number(l.cantidad) > 0 && Number(l.precioUnitario) > 0);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!empresaId) return showToast({ type: "warning", message: "Selecciona una empresa" });
    if (!lineasValidas) {
      return showToast({ type: "warning", message: "Cada línea necesita curso, cantidad y precio por cupo" });
    }

    setSubmitting(true);
    try {
      const res = await cuentasPorCobrarService.crear({
        deudor_tipo: "empresa",
        deudor_id: empresaId,
        observaciones: observaciones.trim() || undefined,
        lineas: lineas.map((l) => ({
          curso_id: l.cursoId,
          cantidad: Number(l.cantidad),
          precio_unitario: Number(l.precioUnitario),
        })),
      });
      showToast({ type: "success", message: `Factura ${res.contifico_numero_factura} emitida en Contífico correctamente` });
      onSuccess?.(res);
      setResultado(res);
    } catch (err) {
      showToast({ type: "danger", message: err?.response?.data?.message || err.message || "Error al facturar a crédito" });
    } finally {
      setSubmitting(false);
    }
  };

  if (resultado) {
    return (
      <>
        <div className="modal-body pt-3 text-center">
          <i className="bi bi-check-circle-fill text-success" style={{ fontSize: "2.5rem" }} />
          <h6 className="mt-3 mb-1">Factura a crédito emitida</h6>
          <p className="text-muted small mb-1">
            {empresaNombre} — {currency(resultado.monto_total)}
          </p>
          <p className="text-muted small mb-0">
            N° de factura Contífico: <strong>{resultado.contifico_numero_factura}</strong>
          </p>
          <p className="text-muted small mt-2">
            Cuando matricules a los empleados, usa "Aplicar cupos" sobre esta cuenta por cobrar
            para consumir el crédito ya facturado sin generar otra factura.
          </p>
        </div>
        <div className="modal-footer border-0 justify-content-center">
          <button type="button" className="btn management-btn-primary" onClick={onClose}>Listo</button>
        </div>
      </>
    );
  }

  return (
    <form onSubmit={handleSubmit}>
      <div className="modal-body pt-3">
        <div className="alert alert-info small mb-3">
          Esto emite la factura en Contífico <strong>de inmediato, a crédito</strong> (sin cobro), antes de
          matricular a nadie. Úsalo cuando la empresa pide que le facturen primero y pagará después.
        </div>
        <div className="row g-3">
          <div className="col-12">
            <SearchableSelect
              label="Empresa *"
              value={empresaId}
              displayValue={empresaNombre}
              options={empresasSugeridas}
              searchTerm={empresaSearch}
              onSearch={(v) => { setEmpresaSearch(v); if (!v) { setEmpresaId(null); setEmpresaNombre(""); } }}
              onSelect={(opt) => { setEmpresaId(opt.id); setEmpresaNombre(opt.label); setEmpresaSearch(""); }}
              placeholder="Buscar empresa..."
            />
          </div>

          <div className="col-12">
            <div className="d-flex justify-content-between align-items-center mb-2">
              <p className="management-eyebrow mb-0">Cursos/niveles y cupos acordados</p>
              <button type="button" className="btn btn-sm btn-outline-secondary" onClick={agregarLinea}>
                <i className="bi bi-plus-lg me-1" />Agregar línea
              </button>
            </div>

            {lineas.map((linea) => (
              <div key={linea._id} className="row g-2 align-items-end mb-2 p-2 border rounded">
                <div className="col-12 col-md-5">
                  <SearchableSelect
                    label="Curso/nivel *"
                    value={linea.cursoId}
                    displayValue={linea.cursoNombre}
                    options={cursosSugeridosPorLinea(linea.cursoSearch)}
                    searchTerm={linea.cursoSearch}
                    onSearch={(v) => actualizarLinea(linea._id, { cursoSearch: v, ...(!v ? { cursoId: null, cursoNombre: "" } : {}) })}
                    onSelect={(opt) => actualizarLinea(linea._id, { cursoId: opt.id, cursoNombre: opt.label, cursoSearch: "" })}
                    placeholder="Buscar curso..."
                  />
                </div>
                <div className="col-6 col-md-2">
                  <label className="form-label small fw-semibold">Cupos *</label>
                  <input
                    type="number" min="1" step="1" className="form-control"
                    value={linea.cantidad}
                    onChange={(e) => actualizarLinea(linea._id, { cantidad: e.target.value })}
                  />
                </div>
                <div className="col-6 col-md-3">
                  <label className="form-label small fw-semibold">Precio por cupo *</label>
                  <div className="input-group">
                    <span className="input-group-text">$</span>
                    <input
                      type="number" min="0.01" step="0.01" className="form-control"
                      value={linea.precioUnitario}
                      onChange={(e) => actualizarLinea(linea._id, { precioUnitario: e.target.value })}
                      placeholder="Con IVA incluido"
                    />
                  </div>
                </div>
                <div className="col-12 col-md-2 text-end">
                  <button
                    type="button" className="btn btn-sm btn-outline-danger"
                    onClick={() => quitarLinea(linea._id)}
                    disabled={lineas.length === 1}
                  >
                    <i className="bi bi-trash" />
                  </button>
                </div>
              </div>
            ))}

            <div className="text-end mt-2">
              <span className="management-eyebrow me-2">Total estimado</span>
              <strong className="fs-5">{currency(montoEstimado)}</strong>
            </div>
          </div>

          <div className="col-12">
            <label className="form-label small fw-semibold">Observaciones</label>
            <textarea
              className="form-control" rows={2}
              value={observaciones}
              onChange={(e) => setObservaciones(e.target.value)}
              placeholder="Ej: Convenio 5 cupos N1 + 6 cupos N2, empleados por confirmar"
            />
          </div>
        </div>
      </div>
      <div className="modal-footer border-0">
        <button type="button" className="btn btn-outline-secondary" onClick={onClose} disabled={submitting}>
          Cancelar
        </button>
        <button type="submit" className="btn management-btn-primary" disabled={submitting}>
          {submitting ? "Facturando..." : "Facturar a crédito en Contífico"}
        </button>
      </div>
    </form>
  );
}

// ══════════════════════════════════════════════════════════════
// MODO: APLICAR — consumir cupos ya facturados con matrículas reales
// ══════════════════════════════════════════════════════════════
function AplicarCupos({ cuentaId, onClose, onSuccess }) {
  const [registro, setRegistro] = useState(null);
  const [cargando, setCargando] = useState(true);
  const [query, setQuery] = useState("");
  const [resultados, setResultados] = useState([]);
  const [buscando, setBuscando] = useState(false);
  const [seleccionados, setSeleccionados] = useState(new Set());
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    cuentasPorCobrarService.obtenerPorId(cuentaId)
      .then(setRegistro)
      .catch(() => showToast({ type: "danger", message: "No se pudo cargar la cuenta por cobrar" }))
      .finally(() => setCargando(false));
  }, [cuentaId]);

  useEffect(() => {
    if (query.trim().length < 2) { setResultados([]); return; }
    const timer = setTimeout(async () => {
      setBuscando(true);
      try {
        const data = await buscarMatriculas({ search: query, estado_pago: "pendiente,parcial", limit: 30 });
        setResultados(Array.isArray(data) ? data : (data?.items || []));
      } catch { setResultados([]); }
      finally { setBuscando(false); }
    }, 350);
    return () => clearTimeout(timer);
  }, [query]);

  const toggleMatricula = (id) => setSeleccionados((prev) => {
    const next = new Set(prev);
    next.has(id) ? next.delete(id) : next.add(id);
    return next;
  });
  const toggleAll = () => {
    const allIds = resultados.map((m) => m.id);
    const todos = allIds.every((id) => seleccionados.has(id));
    setSeleccionados(todos ? new Set() : new Set(allIds));
  };

  const handleAplicar = async () => {
    if (seleccionados.size === 0) return showToast({ type: "warning", message: "Selecciona al menos una matrícula" });
    setSubmitting(true);
    try {
      const res = await cuentasPorCobrarService.aplicarCredito(cuentaId, [...seleccionados]);
      showToast({
        type: "success",
        message: `Cupo aplicado a ${res.pagos?.length || 0} matrícula${res.pagos?.length !== 1 ? "s" : ""}, sin generar factura nueva`
      });
      onSuccess?.(res);
      onClose();
    } catch (err) {
      showToast({ type: "danger", message: err?.response?.data?.message || err.message || "Error al aplicar el cupo" });
    } finally {
      setSubmitting(false);
    }
  };

  if (cargando) {
    return <div className="modal-body text-center py-5"><div className="spinner-border spinner-border-sm text-primary" /></div>;
  }
  if (!registro) {
    return <div className="modal-body"><p className="text-danger">No se encontró la cuenta por cobrar.</p></div>;
  }

  const saldoDisponible = Number(registro.saldo_disponible || 0);

  return (
    <>
      <div className="modal-body pt-3">
        <div className="management-card mb-3 p-3">
          <div className="row g-2 text-center">
            <div className="col-4">
              <p className="management-eyebrow mb-0">Empresa</p>
              <p className="fw-semibold small mb-0">{registro.deudor_nombre || `#${registro.deudor_id}`}</p>
            </div>
            <div className="col-4">
              <p className="management-eyebrow mb-0">Factura Contífico</p>
              <p className="fw-semibold small mb-0">{registro.contifico_numero_factura || "—"}</p>
            </div>
            <div className="col-4">
              <p className="management-eyebrow mb-0">Saldo de cupos</p>
              <p className={`fw-bold mb-0 ${saldoDisponible > 0 ? "text-success" : "text-muted"}`}>
                {currency(saldoDisponible)}
              </p>
            </div>
          </div>
        </div>

        <p className="management-eyebrow mb-2">Cupos disponibles por curso</p>
        <div className="table-responsive mb-3">
          <table className="table table-sm">
            <thead><tr><th>Curso</th><th className="text-end">Cupos</th><th className="text-end">Usados</th><th className="text-end">Disponibles</th></tr></thead>
            <tbody>
              {(registro.detalle || []).map((d) => (
                <tr key={d.id}>
                  <td>{d.curso_nombre}</td>
                  <td className="text-end">{d.cantidad}</td>
                  <td className="text-end">{d.cantidad_consumida}</td>
                  <td className="text-end">
                    <span className={d.cupo_disponible > 0 ? "text-success fw-semibold" : "text-muted"}>
                      {d.cupo_disponible}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {saldoDisponible <= 0 ? (
          <div className="alert alert-warning mb-0">Esta cuenta por cobrar ya no tiene cupos disponibles.</div>
        ) : (
          <>
            <p className="management-eyebrow mb-2">Seleccionar matrículas a cubrir</p>
            <p className="text-muted small mb-2">
              Solo se aplicará a matrículas cuyo curso coincida con alguna línea con cupo disponible arriba.
            </p>
            <MatriculasPicker
              seleccionados={seleccionados}
              onToggle={toggleMatricula}
              onToggleAll={toggleAll}
              resultados={resultados}
              buscando={buscando}
              query={query}
              onQuery={setQuery}
            />
          </>
        )}
      </div>
      <div className="modal-footer border-0">
        <button type="button" className="btn btn-outline-secondary" onClick={onClose} disabled={submitting}>
          Cerrar
        </button>
        {saldoDisponible > 0 && (
          <button
            type="button" className="btn management-btn-primary"
            onClick={handleAplicar} disabled={submitting || seleccionados.size === 0}
          >
            {submitting ? "Aplicando..." : `Aplicar cupo a ${seleccionados.size || 0} matrícula${seleccionados.size !== 1 ? "s" : ""}`}
          </button>
        )}
      </div>
    </>
  );
}

// ══════════════════════════════════════════════════════════════
// MODO: COBRO — registrar el dinero que la empresa efectivamente pagó
// ══════════════════════════════════════════════════════════════
function RegistrarCobro({ cuentaId, onClose, onSuccess }) {
  const [registro, setRegistro] = useState(null);
  const [cargando, setCargando] = useState(true);
  const [metodos, setMetodos] = useState([]);
  const [monto, setMonto] = useState("");
  const [metodoId, setMetodoId] = useState(null);
  const [referencia, setReferencia] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    cuentasPorCobrarService.obtenerPorId(cuentaId)
      .then((data) => { setRegistro(data); setMonto(String(Number(data.saldo_por_cobrar || 0) || "")); })
      .catch(() => showToast({ type: "danger", message: "No se pudo cargar la cuenta por cobrar" }))
      .finally(() => setCargando(false));
    listarMetodos().then((data) => setMetodos(Array.isArray(data) ? data : [])).catch(() => {});
  }, [cuentaId]);

  const handleRegistrar = async () => {
    if (!monto || Number(monto) <= 0) return showToast({ type: "warning", message: "Ingresa un monto válido" });
    if (!metodoId) return showToast({ type: "warning", message: "Selecciona un método de pago" });
    setSubmitting(true);
    try {
      const res = await cuentasPorCobrarService.registrarCobro(cuentaId, {
        monto: Number(monto),
        metodo_id: metodoId,
        referencia_externa: referencia.trim() || undefined,
      });
      showToast({ type: "success", message: `Cobro de ${currency(res.monto_cobrado)} registrado en Contífico` });
      onSuccess?.(res);
      onClose();
    } catch (err) {
      showToast({ type: "danger", message: err?.response?.data?.message || err.message || "Error al registrar el cobro" });
    } finally {
      setSubmitting(false);
    }
  };

  if (cargando) {
    return <div className="modal-body text-center py-5"><div className="spinner-border spinner-border-sm text-primary" /></div>;
  }
  if (!registro) {
    return <div className="modal-body"><p className="text-danger">No se encontró la cuenta por cobrar.</p></div>;
  }

  const saldoPorCobrar = Number(registro.saldo_por_cobrar || 0);

  return (
    <>
      <div className="modal-body pt-3">
        <div className="management-card mb-3 p-3">
          <div className="row g-2 text-center">
            <div className="col-4">
              <p className="management-eyebrow mb-0">Factura Contífico</p>
              <p className="fw-semibold small mb-0">{registro.contifico_numero_factura || "—"}</p>
            </div>
            <div className="col-4">
              <p className="management-eyebrow mb-0">Monto total</p>
              <p className="fw-bold mb-0">{currency(registro.monto_total)}</p>
            </div>
            <div className="col-4">
              <p className="management-eyebrow mb-0">Saldo por cobrar</p>
              <p className={`fw-bold mb-0 ${saldoPorCobrar > 0 ? "text-warning" : "text-success"}`}>
                {currency(saldoPorCobrar)}
              </p>
            </div>
          </div>
        </div>

        {saldoPorCobrar <= 0 ? (
          <div className="alert alert-success mb-0">Esta factura ya está completamente cobrada.</div>
        ) : (
          <div className="row g-3">
            <div className="col-12 col-md-6">
              <label className="form-label small fw-semibold">Monto recibido *</label>
              <div className="input-group">
                <span className="input-group-text">$</span>
                <input
                  type="number" min="0.01" step="0.01" className="form-control"
                  value={monto} onChange={(e) => setMonto(e.target.value)} placeholder="0.00"
                />
              </div>
            </div>
            <div className="col-12 col-md-6">
              <label className="form-label small fw-semibold">Método de pago *</label>
              <select className="form-select" value={metodoId || ""} onChange={(e) => setMetodoId(Number(e.target.value) || null)}>
                <option value="">Seleccionar...</option>
                {metodos.map((m) => <option key={m.id} value={m.id}>{m.nombre}</option>)}
              </select>
            </div>
            <div className="col-12">
              <label className="form-label small fw-semibold">Referencia (N° transferencia, depósito, etc.)</label>
              <input
                type="text" className="form-control"
                value={referencia} onChange={(e) => setReferencia(e.target.value)}
              />
            </div>
          </div>
        )}
      </div>
      <div className="modal-footer border-0">
        <button type="button" className="btn btn-outline-secondary" onClick={onClose} disabled={submitting}>
          Cerrar
        </button>
        {saldoPorCobrar > 0 && (
          <button type="button" className="btn management-btn-primary" onClick={handleRegistrar} disabled={submitting}>
            {submitting ? "Registrando..." : "Registrar cobro en Contífico"}
          </button>
        )}
      </div>
    </>
  );
}

// ══════════════════════════════════════════════════════════════
// EXPORT PRINCIPAL
// mode: "crear" | "aplicar" | "cobro"
// cuentaId: requerido en mode="aplicar" o "cobro"
// ══════════════════════════════════════════════════════════════
export default function CuentaPorCobrarModal({ open, mode = "crear", cuentaId, onClose, onSuccess }) {
  const titles = {
    crear: "Facturar a crédito",
    aplicar: "Aplicar cupos facturados",
    cobro: "Registrar cobro",
  };
  const subtitles = {
    crear: "Emite la factura en Contífico antes de matricular a nadie",
    aplicar: "Consume cupos ya facturados al matricular gente real",
    cobro: "Registra el dinero que la empresa pagó contra la factura ya emitida",
  };

  return (
    <ModalWrapper open={open} title={titles[mode] || titles.crear} subtitle={subtitles[mode] || ""} onClose={onClose}>
      {mode === "crear" && <CrearCuentaPorCobrar onClose={onClose} onSuccess={onSuccess} />}
      {mode === "aplicar" && cuentaId && (
        <AplicarCupos cuentaId={cuentaId} onClose={onClose} onSuccess={onSuccess} />
      )}
      {mode === "cobro" && cuentaId && (
        <RegistrarCobro cuentaId={cuentaId} onClose={onClose} onSuccess={onSuccess} />
      )}
    </ModalWrapper>
  );
}
